






















/* eslint-disable @typescript-eslint/camelcase */
import { DateTime } from 'luxon';
import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator';
import HexabaseApi from '@/services/hexabase/hexabaseApi';
import hexabaseState from '@/store/hexabase';
import authState from '@/store/auth';
import Table from '@/components/common/elements/Table.vue';
import DialogDetails from '@/components/common/modules/DialogDetails.vue';
import ConstructionModel from '@/models/constructionModel';
import ConstructionSubject from '@/models/data/constructionSubject';
import Customer from '@/models/data/customer';
import Department from '@/models/data/department';
import Staff from '@/models/data/staff';
import Material from '@/models/data/material';
import Supplier from '@/models/data/supplier';

import { optionalFormInVoice, masterControlColums } from '@/constants/form/form';
import { FieldChildrenProp, FieldsInfo, ItemActions } from '@/services/hexabase/types';
import { InputProps } from '@/constants/form/types';
import {
  periodProp,
  constructionIdProp,
  DetailNotFoundMessage
} from '@/constants/construction/construction';

import {
  ViewType,
  TabTest,
  MaterialCostColumnFormat,
  LaborCostColumnFormat,
  ConsumableCostColumnFormat,
  FrequentlyUsedWords
} from '@/constants/construction/constructionEdit';

@Component({
  components: {
    Table,
    DialogDetails
  }
})
export default class ListPanelComponent extends Vue {
  private constructionModel = new ConstructionModel();
  private hexabaseApi = new HexabaseApi();
  private tableHeader: {}[] = [];
  private tableData: Array<any> = [];
  // ダイアログの表示ラベル名
  private dialogLabel = '';
  // ダイアログの表示制御値
  private dialogStatus = false;
  // ダイアログの表示メッセージ
  private detailDialogMessage = '';

  // ダイアログ内の明細データ
  private currentDetailData: any = [];
  private detailFieldsData: Array<Array<FieldChildrenProp>> = [];
  private detailFieldCols = '';

  // アクション情報格納プロパティ
  private itemActions: Array<ItemActions> = [];

  private masterData: { [k: string]: { [k: string]: Array<{ [k: string]: string }> } } = {};

  private constructionSubject = new ConstructionSubject();
  private customer = new Customer();
  private department = new Department();
  private staff = new Staff();
  private material = new Material();
  private supplier = new Supplier();

  /*
   * tableHeaderWithType
   * この変数を定義した目的
   *  (1) ヘッダーを生成する
   *  (2) カスタムソートのソートタイプを判別するためのtypeをヘッダーと紐づける
   *
   * text：テーブルカラムに表示するテキスト
   * value：カラムを特定する一意な値
   * width：カラムの幅
   * type：ソートタイプ（stringがデフォルト※通常の文字列）
   * ※typeを追加したい場合は、「customSort」に処理を追加する必要がある
   * ※ソートが不要な列に対しては、typeに''（空文字）を指定し、sortable:falseを追加する
   *
   */
  private tableHeaderWithType: Array<{
    text: string;
    width: string;
    value: string;
    type: string;
    sortable?: boolean;
  }> = [
    { text: '請求確認', width: '10px', value: 'action', type: '', sortable: false },
    {
      text: '工事管理番号',
      width: '10px',
      value: 'construction_management_no',
      type: 'construction_management_no'
    },
    { text: '工事名', width: '10px', value: 'title_disp', type: 'string' },
    { text: '受注予定額', width: '10px', value: 'estimate_order_price', type: 'number' },
    { text: '予定原価率', width: '10px', value: 'estimate_cost_rate', type: 'number' },
    { text: '受注確定額', width: '10px', value: 'fixed_order_price', type: 'number' },
    { text: '材料費', width: '10px', value: 'total_material_cost', type: 'number' },
    { text: '労務費', width: '10px', value: 'total_labor_cost', type: 'number' },
    { text: 'その他経費', width: '10px', value: 'total_other_cost', type: 'number' },
    { text: '工事原価', width: '10px', value: 'total_construction_cost', type: 'number' },
    { text: '原価率', width: '10px', value: 'construction_cost_rate', type: 'number' },
    { text: '元請名', width: '10px', value: 'customer_name', type: 'string' },
    { text: '請求先', width: '10px', value: 'billing_destination_name', type: 'string' },
    { text: '工事区分', width: '10px', value: 'orderer_class', type: 'string' },
    { text: '請負区分', width: '10px', value: 'contract_class', type: 'string' },
    { text: '部門', width: '10px', value: 'construction_division_name', type: 'string' },
    { text: '担当者', width: '10px', value: 'sales_staff_name', type: 'string' },
    { text: '締日', width: '10px', value: 'closing_day', type: 'string' },
    { text: '回収予定', width: '10px', value: 'collection_day', type: 'string' },
    { text: '完成予定月', width: '10px', value: 'scheduled_completion_date', type: 'date' },
    { text: '完成締日', width: '10px', value: 'estimate_completion_date', type: 'date' },
    { text: '請求累計', width: '10px', value: 'fixed_order_price', type: 'number' },
    { text: '請求残', width: '10px', value: 'billing_balance', type: 'number' }
  ];
  private readonly customSort: Function = (
    items: any[],
    sortByDatas: string[],
    isDescDatas: boolean[]
  ) => {
    const sortBy: string = sortByDatas[0]; // 列名
    const isDesc: boolean = isDescDatas[0]; // 降順：true, 昇順：false
    const tableColumnWithType = this.tableHeaderWithType.find(v => v.value === sortBy);

    items.sort((a, b) => {
      let sort = 1;

      if (a[sortBy] === b[sortBy]) {
        sort = 0;
      } else if (tableColumnWithType?.type === 'construction_management_no') {
        const a_split = a[sortBy].split('-'); // 値の例：['62', '487', '7']
        const b_split = b[sortBy].split('-');

        // 左から順に比較(期→工事番号→工事科目の順に比較)
        if (a_split[0] == b_split[0]) {
          if (a_split[1] == b_split[1]) {
            if (Number(a_split[2]) < Number(b_split[2])) {
              sort = -1;
            }
          } else {
            if (Number(a_split[1]) < Number(b_split[1])) {
              sort = -1;
            }
          }
        } else {
          if (Number(a_split[0]) < Number(b_split[0])) {
            sort = -1;
          }
        }
      } else if (tableColumnWithType?.type === 'number') {
        // 空の場合は、昇順・降順に関わらず下へソート
        if (a[sortBy] === undefined && b[sortBy] === undefined) return 0;
        if (a[sortBy] === '' || a[sortBy] === undefined) return 1;
        if (b[sortBy] === '' || b[sortBy] === undefined) return -1;

        // 数値として比較
        if (Number(a[sortBy]) < Number(b[sortBy])) {
          sort = -1;
        }
      } else if (tableColumnWithType?.type === 'date') {
        // 空の場合は、昇順・降順に関わらず下へソート
        if (a[sortBy] === undefined && b[sortBy] === undefined) return 0;
        if (a[sortBy] === '' || a[sortBy] === undefined) return 1;
        if (b[sortBy] === '' || b[sortBy] === undefined) return -1;

        // 整形が必要な日付フォーマットを整形（例：2022-01だったら2022-01-01にする）
        const regex = new RegExp(/^\d{4}-\d{2}$/);
        let formatedA = a[sortBy];
        let formatedB = b[sortBy];
        if (regex.test(a[sortBy])) formatedA = a[sortBy] + '-01';
        if (regex.test(b[sortBy])) formatedB = b[sortBy] + '-01';

        const formatedAForCompare = DateTime.fromISO(formatedA);
        const formatedBForCompare = DateTime.fromISO(formatedB);

        // 日付として比較
        if (formatedAForCompare < formatedBForCompare) {
          sort = -1;
        }
      } else {
        // その他('string'も同様)
        if (typeof a[sortBy] !== 'undefined' && typeof b[sortBy] !== 'undefined') {
          if (a[sortBy].toLowerCase().localeCompare(b[sortBy].toLowerCase()) < 0) {
            sort = -1;
          }
        }
      }

      // 降順の場合はソート順を反転
      if (!isDesc) {
        return sort;
      } else {
        return sort * -1;
      }
    });
    return items;
  };

  @Prop() viewName?: string;
  @Prop() lastTargetPeriod?: string;

  @Watch('lastTargetPeriod')
  async onlastTargetPeriodChanged(
    newlastTargetPeriodChanged: string,
    oldlastTargetPeriodChanged: string
  ) {
    this.createSalesListData(newlastTargetPeriodChanged);
  }

  private async created() {
    // マスターデータを取得しグローバル変数に格納
    await this.getMasterData();
  }

  private async mounted() {
    this.tableHeader = this.tableHeaderWithType.map(v => {
      // tableHeaderWithTypeから必要な値だけ取り出してを生成する
      const forHeaderData: { [k: string]: any } = {
        text: v.text,
        width: v.width,
        value: v.value
      };
      if (v.sortable !== undefined) forHeaderData.sortable = v.sortable;
      return forHeaderData;
    });
  }

  async clickInvoiceDetail(item: any) {
    try {
      this.$store.commit('hexabase/setIsLoading', true);
      this.dialogLabel =
        item.construction_management_no + ' ' + (item.title_disp ? item.title_disp : '');
      // 既存アイテムを取得しアイテム情報・アクション情報を保持
      const constructionBasicResult = await this.constructionModel.getLinkedItems(
        hexabaseState.applicationId,
        hexabaseState.datastoreIds['工事'],
        item.i_id
      );
      constructionBasicResult.item_actions.forEach(v => this.itemActions.push(v));
      // 明細入力エリア作成用に値を保持
      this.currentDetailData =
        constructionBasicResult.linked_items[hexabaseState.datastoreIds['請求書']];
      await this.getDetailFields();
      if (this.detailFieldsData.length === 0) {
        this.detailDialogMessage = DetailNotFoundMessage;
      } else {
        this.detailDialogMessage = '';
      }
      this.dialogStatus = true;
    } catch (error) {
      console.error(error);
    } finally {
      this.$store.commit('hexabase/setIsLoading', false);
    }
  }

  dialogClose() {
    this.dialogStatus = false;
  }

  /**
   * 明細入力フォームを生成するための設定情報を取得・整形します
   */
  private async getDetailFields() {
    try {
      this.detailFieldsData = [];
      const relatedDsId = hexabaseState.datastoreIds['請求書'];
      const newAction = await this.constructionModel.getNewActionList(relatedDsId).then(data => {
        return data;
      });
      const inputFields = await this.constructionModel
        .getFieldsByActionId(relatedDsId, newAction.actions[0].action_id)
        .then(data => {
          return data;
        });
      const data = await this.constructionModel
        .makeFieldsList(hexabaseState.applicationId, relatedDsId)
        .then(data => {
          return this.constructionModel
            .addOptionalFormField(data, JSON.parse(JSON.stringify(optionalFormInVoice)))
            .map(field => this.constructionModel.inputRestriction(field, inputFields))
            .map(field => this.constructionModel.addOptionalMasterData(field, this.masterData));
        });
      const maxColRecode = this.constructionModel.getMaxcolRecode(data);
      const colCount = maxColRecode.col! + maxColRecode.sizeX!;
      const colWidth = 100 / colCount;
      this.detailFieldCols = `${colWidth}% `.repeat(colCount);

      const detailAreas: Array<Array<
        FieldChildrenProp
      >> = this.constructionModel.getCurrentDetailArea(this.currentDetailData, data);
      this.detailFieldsData = detailAreas.length === 0 ? [] : detailAreas;
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * 各種入力フォームで使用するマスタを取得します
   */
  private async getMasterData() {
    const mData: { [k: string]: { [k: string]: Array<{ [k: string]: string }> } } = {};
    mData['m_construction_subject'] = await this.constructionSubject
      .getData()
      .then(data => this.constructionModel.modifyMasterData(data, 'id', 'm_construction_subject'));
    mData['m_customer'] = await this.customer
      .getData()
      .then(data => this.constructionModel.modifyMasterData(data, 'id', 'm_customer'));
    mData['m_department'] = await this.department
      .getData()
      .then(data => this.constructionModel.modifyMasterData(data, 'id', 'm_department'));
    mData['m_staff'] = await this.staff
      .getData()
      .then(data => this.constructionModel.modifyMasterData(data, 'id', 'm_staff'));
    this.masterData = mData;
  }

  private async createSalesListData(targetPeriod: string) {
    try {
      this.$store.commit('hexabase/setIsLoading', true);
      await this.getMasterData();
      const constructionParams = {
        conditions: [
          {
            id: 'period',
            search_value: [targetPeriod, targetPeriod],
            exact_match: true
          }
        ],
        page: 1,
        per_page: 0,
        use_display_id: true,
        sort_fields: [
          { id: 'construction_no', order: 'desc' },
          { id: 'construction_subject_id', order: 'desc' }
        ]
      };
      // 工事テーブルからデータ取得
      const constructionResult = await this.hexabaseApi.getItems(
        authState.token,
        hexabaseState.applicationId,
        hexabaseState.datastoreIds['工事'],
        constructionParams
      );
      const invoiceParams = {
        conditions: [],
        page: 1,
        per_page: 0,
        use_display_id: true
      };
      // 請求書テーブルからデータ取得
      const invoiceResult = await this.hexabaseApi.getItems(
        authState.token,
        hexabaseState.applicationId,
        hexabaseState.datastoreIds['請求書'],
        invoiceParams
      );
      // 期と工事番号でグルーピングし、合計請求金額を算出する
      const invoiceResultGroupBy = invoiceResult.reduce(function(result: any, current: any) {
        const element = result.find(function(p: any) {
          return p.period === current.period && p.construction_no === current.construction_no;
        });
        if (element) {
          element.count++; // count
          element.billing_amount += Number(current.billing_amount);
        } else {
          result.push({
            period: current.period,
            construction_no: current.construction_no,
            count: 1,
            billing_amount: Number(current.billing_amount)
          });
        }
        return result;
      }, []);
      const materialCostParams = {
        conditions: [],
        page: 1,
        per_page: 0,
        use_display_id: true
      };
      // 確定原価材料費明細テーブルからデータ取得
      const materialCostResult = await this.hexabaseApi.getItems(
        authState.token,
        hexabaseState.applicationId,
        hexabaseState.datastoreIds['確定原価材料費明細'],
        materialCostParams
      );
      const laborCostParams = {
        conditions: [],
        page: 1,
        per_page: 0,
        use_display_id: true
      };
      // 確定原価労務費明細テーブルからデータ取得
      const laborCostResult = await this.hexabaseApi.getItems(
        authState.token,
        hexabaseState.applicationId,
        hexabaseState.datastoreIds['確定原価労務費明細'],
        laborCostParams
      );
      const otherCostParams = {
        conditions: [],
        page: 1,
        per_page: 0,
        use_display_id: true
      };
      // 確定原価その他経費明細テーブルからデータ取得
      const otherCostResult = await this.hexabaseApi.getItems(
        authState.token,
        hexabaseState.applicationId,
        hexabaseState.datastoreIds['確定原価その他経費明細'],
        otherCostParams
      );
      const estimateMaterialCostParams = {
        conditions: [],
        page: 1,
        per_page: 0,
        use_display_id: true
      };
      // 予定原価材料費明細テーブルからデータ取得
      const estimateMaterialCostResult = await this.hexabaseApi.getItems(
        authState.token,
        hexabaseState.applicationId,
        hexabaseState.datastoreIds['予定原価材料費明細'],
        estimateMaterialCostParams
      );
      const estimateLaborCostParams = {
        conditions: [],
        page: 1,
        per_page: 0,
        use_display_id: true
      };
      // 予定原価労務費明細テーブルからデータ取得
      const estimateLaborCostResult = await this.hexabaseApi.getItems(
        authState.token,
        hexabaseState.applicationId,
        hexabaseState.datastoreIds['予定原価労務費明細'],
        estimateLaborCostParams
      );
      const estimateOtherCostParams = {
        conditions: [],
        page: 1,
        per_page: 0,
        use_display_id: true
      };
      // 予定原価その他経費明細テーブルからデータ取得
      const estimateOtherCostResult = await this.hexabaseApi.getItems(
        authState.token,
        hexabaseState.applicationId,
        hexabaseState.datastoreIds['予定原価その他経費明細'],
        estimateOtherCostParams
      );

      this.tableData = constructionResult;
      for (const key in this.tableData) {
        this.tableData[key]['construction_management_no'] =
          this.tableData[key]['period'] +
          '-' +
          this.tableData[key]['construction_no'] +
          '-' +
          this.tableData[key]['construction_subject_id'];
        this.tableData[key]['scheduled_completion_date'] = this.constructionModel.editDatetype(
          this.tableData[key]['scheduled_completion_date'],
          'Asia/Tokyo',
          'yyyy-MM'
        );
        this.tableData[key]['estimate_completion_date'] = this.constructionModel.editDatetype(
          this.tableData[key]['estimate_completion_date'],
          'Asia/Tokyo',
          'yyyy-MM-dd'
        );
        const constructionDivisionName = this.masterData['m_department'].body.find(
          v => String(v.id) === String(this.tableData[key].construction_division_id)
        );
        if (constructionDivisionName) {
          this.tableData[key]['construction_division_name'] = constructionDivisionName.name;
        } else {
          this.tableData[key]['construction_division_name'] = '';
        }
        const salesStaffName = this.masterData['m_staff'].body.find(
          v => String(v.id) === String(this.tableData[key].sales_staff_id)
        );
        if (salesStaffName) {
          this.tableData[key]['sales_staff_name'] = salesStaffName.display_name;
        } else {
          this.tableData[key]['sales_staff_name'] = '';
        }
        const customerName = this.masterData['m_customer'].body.find(
          v => String(v.id) === String(this.tableData[key].customer_id)
        );
        if (customerName) {
          this.tableData[key]['customer_name'] = customerName.name;
        } else {
          this.tableData[key]['customer_name'] = '';
        }
        const closingDay = this.masterData['m_customer'].body.find(
          v => String(v.id) === String(this.tableData[key].customer_id)
        );
        if (closingDay) {
          this.tableData[key]['closing_day'] = closingDay.closing_day;
        } else {
          this.tableData[key]['closing_day'] = '';
        }
        const collectionDay = this.masterData['m_customer'].body.find(
          v => String(v.id) === String(this.tableData[key].customer_id)
        );
        if (collectionDay) {
          this.tableData[key]['collection_day'] = collectionDay.collection_day;
        } else {
          this.tableData[key]['collection_day'] = '';
        }
        const invoiceTotalAmount = invoiceResultGroupBy.find((p: any) => {
          return (
            p.period === this.tableData[key].period &&
            p.construction_no === this.tableData[key].construction_no
          );
        });
        if (!isNaN(this.tableData[key].fixed_order_price) && invoiceTotalAmount) {
          this.tableData[key]['billing_balance'] =
            Number(this.tableData[key].fixed_order_price) -
            Number(invoiceTotalAmount.billing_amount);
        }
        // 確定原価
        this.tableData[key]['total_material_cost'] = 0;
        materialCostResult.forEach((item: any) => {
          if(item.period === this.tableData[key]['period'] && item.construction_no === this.tableData[key]['construction_no']){
            this.tableData[key]['total_material_cost'] += Number(item.unit_price) * Number(item.quantity);
          }
          if (isNaN(this.tableData[key]['total_material_cost'])) this.tableData[key]['total_material_cost'] = 0;
        });
        this.tableData[key]['total_labor_cost'] = 0;
        laborCostResult.forEach((item: any) => {
          if(item.period === this.tableData[key]['period'] && item.construction_no === this.tableData[key]['construction_no']){
            this.tableData[key]['total_labor_cost'] += Number(item.unit_price) * Number(item.quantity);
          }
          if (isNaN(this.tableData[key]['total_labor_cost'])) this.tableData[key]['total_labor_cost'] = 0;
        });
        this.tableData[key]['total_other_cost'] = 0;
        otherCostResult.forEach((item: any) => {
          if(item.period === this.tableData[key]['period'] && item.construction_no === this.tableData[key]['construction_no']){
            this.tableData[key]['total_other_cost'] += Number(item.unit_price) * Number(item.quantity);
          }
          if (isNaN(this.tableData[key]['total_other_cost'])) this.tableData[key]['total_other_cost'] = 0;
        });
        this.tableData[key]['total_construction_cost'] = this.tableData[key]['total_material_cost'] + this.tableData[key]['total_labor_cost'] + this.tableData[key]['total_other_cost'];
        if(!this.tableData[key]['total_construction_cost'] || this.tableData[key]['total_construction_cost'] === '0'
          || !this.tableData[key]['fixed_order_price'] || this.tableData[key]['fixed_order_price'] === '0') {
          this.tableData[key]['construction_cost_rate'] = '';
        } else {
          this.tableData[key]['construction_cost_rate'] = this.tableData[key]['total_construction_cost'] / this.tableData[key]['fixed_order_price'] * 100;
          this.tableData[key]['construction_cost_rate'] = this.tableData[key]['construction_cost_rate'].toFixed(2) + '%';
        }
        // 予定原価
        let totalEstimateMaterialCost = 0;
        estimateMaterialCostResult.forEach((item: any) => {
          if(item.period === this.tableData[key]['period'] && item.construction_no === this.tableData[key]['construction_no']){
            totalEstimateMaterialCost += Number(item.unit_price) * Number(item.quantity);
          }
          if (isNaN(totalEstimateMaterialCost)) totalEstimateMaterialCost = 0;
        });
        let totalEstimateLaborCost = 0;
        estimateLaborCostResult.forEach((item: any) => {
          if(item.period === this.tableData[key]['period'] && item.construction_no === this.tableData[key]['construction_no']){
            totalEstimateLaborCost += Number(item.unit_price) * Number(item.quantity);
          }
          if (isNaN(totalEstimateLaborCost)) totalEstimateLaborCost = 0;
        });
        let totalEstimateotherCost = 0;
        estimateOtherCostResult.forEach((item: any) => {
          if(item.period === this.tableData[key]['period'] && item.construction_no === this.tableData[key]['construction_no']){
            totalEstimateotherCost += Number(item.unit_price) * Number(item.quantity);
          }
          if (isNaN(totalEstimateotherCost)) totalEstimateotherCost = 0;
        });
        const totalEstimateCost = totalEstimateMaterialCost + totalEstimateLaborCost + totalEstimateotherCost;
        if(!totalEstimateCost || !this.tableData[key]['estimate_order_price'] || this.tableData[key]['estimate_order_price'] === '0') {
          this.tableData[key]['estimate_cost_rate'] = '';
        } else {
          this.tableData[key]['estimate_cost_rate'] = totalEstimateCost/ this.tableData[key]['estimate_order_price'] * 100;
          this.tableData[key]['estimate_cost_rate'] = this.tableData[key]['estimate_cost_rate'].toFixed(2) + '%';
        }

        if (isNaN(this.tableData[key]['fixed_order_price'])) this.tableData[key]['fixed_order_price'] = 0;
        this.tableData[key]['fixed_order_price'] = Number(this.tableData[key].fixed_order_price).toLocaleString();
        if (isNaN(this.tableData[key]['estimate_order_price'])) this.tableData[key]['estimate_order_price'] = 0;
        this.tableData[key]['estimate_order_price'] = Number(this.tableData[key].estimate_order_price).toLocaleString();
        this.tableData[key]['total_material_cost'] = Number(this.tableData[key]['total_material_cost']).toLocaleString();
        this.tableData[key]['total_labor_cost'] = Number(this.tableData[key]['total_labor_cost']).toLocaleString();
        this.tableData[key]['total_other_cost'] = Number(this.tableData[key]['total_other_cost']).toLocaleString();
        this.tableData[key]['total_construction_cost'] = Number(this.tableData[key]['total_construction_cost']).toLocaleString();
        if (isNaN(this.tableData[key]['billing_balance'])) this.tableData[key]['billing_balance'] = 0;
        if (this.tableData[key]['billing_balance']) {
          this.tableData[key]['billing_balance'] = Number(this.tableData[key]['billing_balance']).toLocaleString();
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.$store.commit('hexabase/setIsLoading', false);
    }
  }

  private ViewType = ViewType;
}
